import axios from "axios";
import { apiClient, menuwiseApiBaseURL } from "./api_base";

const publicDishClient = axios.create({
    baseURL: menuwiseApiBaseURL,
    headers: {
        Accept: "application/json",
    },
    timeout: 60000,
});

// Create a new dish
export const createDish = async (dishData) => {
    const payload = dishData;
    const hasFile = dishData && typeof dishData === 'object' && dishData.image_file instanceof File;

    if (hasFile) {
        const formData = new FormData();
        Object.entries(dishData).forEach(([key, value]) => {
            if (value === undefined || value === null) return;
            formData.append(key, value);
        });
        const response = await apiClient.post('/dishes/create', formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });
        return response;
    }

    const response = await apiClient.post('/dishes/create', payload);
    return response;
}

// Fetch all dishes for affiliate - MATCHES LARAVEL ROUTE
// Optional params e.g. { public_page_only: true } for Menyer (publik rättsida only)
export const fetchDishes = async (params = {}) => {
    const maxRetries = 2;
    let lastError;
    
    for (let attempt = 0; attempt <= maxRetries; attempt++) {
        try {
            // Primary endpoint matching Laravel route: GET /dishes
            const response = await apiClient.get('/dishes', { params });
            return response;
        } catch (error) {
            lastError = error;
            
            // Don't retry on duplicate request errors
            if (error.message === 'Duplicate request') {
                throw error;
            }
            
            // Don't retry on client errors (4xx)
            if (error.response && error.response.status >= 400 && error.response.status < 500) {
                break;
            }
            
            // Retry with exponential backoff for server errors and network issues
            if (attempt < maxRetries) {
                const delay = Math.pow(2, attempt) * 1000; // 1s, 2s
                console.log(`Retrying dishes fetch in ${delay}ms (attempt ${attempt + 1}/${maxRetries + 1})`);
                await new Promise(resolve => setTimeout(resolve, delay));
                continue;
            }
        }
    }
    
    console.error('Error fetching dishes from /dishes after retries:', lastError);
    throw new Error('Unable to fetch dishes. Please check backend API endpoints.');
}

// Get dishes by category - MATCHES LARAVEL ROUTE
export const fetchDishesByCategory = async (categoryId) => {
    const response = await apiClient.get(`/dishes/category/${categoryId}`);
    return response;
}

// Get ingredients for search/selection - MATCHES LARAVEL ROUTE
export const getIngredients = async (query) => {
    const response = await apiClient.get('/ingredients', {
        params: { query }
    });
    return response;
}

// Update dish name - MATCHES LARAVEL ROUTE
export const updateDishName = async (dishId, dishName) => {
    const response = await apiClient.put(`/dishes/update-name/${dishId}`, { name: dishName });
    return response;
}

// Update dish description - MATCHES LARAVEL ROUTE
export const updateDishDescription = async (dishId, description) => {
    const response = await apiClient.put(`/dishes/update-description/${dishId}`, { description });
    return response;
}

export const updateDishSalesPrice = async (dishId, salesPriceTotal) => {
    const response = await apiClient.put(`/dishes/update-sales-price/${dishId}`, {
        sales_price_total: salesPriceTotal
    });
    return response;
}

// Update dish image - MATCHES LARAVEL ROUTE
export const updateDishImage = async (dishId, imageInput) => {
    const isFile = imageInput instanceof File;
    const payload = isFile ? new FormData() : { image_url: imageInput };

    if (isFile) {
        payload.append('image_file', imageInput);
        payload.append('_method', 'PUT');
    }

    const response = isFile
        ? await apiClient.post(`/dishes/update-image/${dishId}`, payload, {
            headers: { 'Content-Type': 'multipart/form-data' },
        })
        : await apiClient.put(`/dishes/update-image/${dishId}`, payload);
    return response;
}

// Update extra ingredients - MATCHES LARAVEL ROUTE
export const updateExtraIngredients = async (dishId, extraIngredients) => {
    const response = await apiClient.put(`/dishes/update-extra-ingredients/${dishId}`, { 
        extra_ingredients: extraIngredients 
    });
    return response;
}

// Update portion size - MATCHES LARAVEL ROUTE
export const updatePortionSize = async (dishId, portionSize) => {
    const response = await apiClient.put(`/dishes/update-portion-size/${dishId}`, { 
        portion_size: portionSize 
    });
    return response;
}

// Update cooking weight loss percentage - MATCHES LARAVEL ROUTE
export const updateCookingWeightLoss = async (dishId, weightLossPercentage) => {
    const response = await apiClient.put(`/dishes/update-cooking-weight-loss/${dishId}`, { 
        cooking_weight_loss_percentage: weightLossPercentage 
    });
    return response;
}

// Remove dish - MATCHES LARAVEL ROUTE
export const removeDish = async (dishId) => {
    const response = await apiClient.delete(`/dishes/${dishId}`);
    return response;
}

// Fetch dish by ID - MATCHES LARAVEL ROUTE
export const fetchDishById = async (dishId) => {
    const response = await apiClient.get(`/dishes/${dishId}`);
    return response;
}

export const acknowledgeDishProductSyncNotice = async (dishId) => {
    const response = await apiClient.post(`/dishes/${dishId}/ack-product-sync-notice`);
    return response;
}

/** Public consumer dish by unguessable share token (no auth). */
export const fetchPublicDishAppData = async (shareToken) => {
    const response = await publicDishClient.get(`/app-data/share/${shareToken}`);
    return response;
};

export const updateDishPublicPage = async (dishId, isPublicPage) => {
    const response = await apiClient.put(`/dishes/${dishId}/public-page`, {
        is_public_page: !!isPublicPage,
    });
    return response;
};

// Recalculate dish ethics from ingredients (fixes wrong vegan/animal when ingredient data was missing)
export const calculateDishEthics = async (dishId) => {
    const response = await apiClient.post(`/dishes/${dishId}/calculate-ethics`);
    return response;
}

// Fetch on-demand aggregated nutrients for a dish
export const fetchDishNutrients = async (dishId) => {
    const response = await apiClient.get(`/dishes/${dishId}/nutrients`);
    return response;
}

// Add ingredient to dish - MATCHES LARAVEL ROUTE
export const addIngredientToDish = async (dishId, ingredients) => {
    const response = await apiClient.post('/dishes/add-ingredient', {
        dish_id: dishId,
        ingredients: ingredients
    });
    return response;
}

// Get countries for ingredient origin - MATCHES LARAVEL ROUTE
export const getCountries = async () => {
    const response = await apiClient.get('/countries');
    return response;
}

// Component management functions - NEED TO BE ADDED TO LARAVEL ROUTES
export const removeComponentConnection = async (connectionId) => {
    const response = await apiClient.post('/components/remove-connection', { 
        id: connectionId 
    });
    return response;
}

export const editComponentConnection = async (connectionId, editData) => {
    const response = await apiClient.post('/components/edit-connection', { 
        id: connectionId,
        ...editData
    });
    return response;
}

// Search dishes by name (affiliate-owned). Backend endpoint to be added
export const searchDishes = async (query, limit, selfId) => {
    const params = {};
    if (typeof query === 'string' && query.trim().length > 0) {
        params.query = query.trim();
    }
    if (typeof limit === 'number') {
        params.limit = limit;
    }
    if (typeof selfId === 'number' && selfId > 0) {
        params.selfId = selfId;
    }
    const response = await apiClient.get('/dishes/search', { params });
    return response;
}

// Add product components to a dish (optional insert_after_id places them under a row)
export const addProductComponents = async (dishId, components, options = {}) => {
    const payload = {
        dish_id: dishId,
        components,
    };
    if (options.insert_after_id != null) {
        payload.insert_after_id = options.insert_after_id;
    }
    const response = await apiClient.post('/dishes/add-product-component', payload);
    return response;
}

// Add recipe components (child dishes) to a dish (backend endpoint to be added)
export const addRecipeComponents = async (dishId, components) => {
    const response = await apiClient.post('/dishes/add-recipe-component', {
        dish_id: dishId,
        components,
    });
    return response;
}

// Reorder a connection by updating its position
export const updateConnectionPosition = async (connectionId, position) => {
    const response = await apiClient.post('/components/edit-connection', {
        id: connectionId,
        position
    });
    return response;
}

// Update meal attributes for a dish
export const updateMealAttributes = async (dishId, mealData) => {
    const response = await apiClient.put(`/dishes/${dishId}/meal-attributes`, mealData);
    return response;
}

// Update dish visibility (is_public, visibility_scope, customer_ids)
// Clone a dish with its components
export const cloneDish = async (dishId, name) => {
    const response = await apiClient.post(`/dishes/${dishId}/clone`, name ? { name } : {});
    return response;
}
